import I18n from "i18n-js";
import React, { Component } from "react";
import { bool, func, shape } from "prop-types";
import { connect } from "react-redux";
import TextInput from "../../common/components/form/TextInput";
import Select from "../../common/components/form/Select";
import SubmitButton from "../../common/components/form/SubmitButton";
import { getPressureGaugesList } from "../actions/pressureGaugesActions";
import ClientTypes from "../../company/models/ClientTypes";
import { alphabetically } from "../../common/utils/filterUtils";
import DateInput from "../../common/components/form/DateInput";
import ClientUtils from "../../company/utils/ClientUtils";

class CreatePressureGaugeForm extends Component {
  static propTypes = {
    getPressureGaugesList: func.isRequired,
    clients: shape().isRequired,
    pressureGauge: shape().isRequired,
    pressureGauges: shape().isRequired,
    infos: shape(),
    loading: bool,
    onSubmit: func.isRequired,
    goBack: func.isRequired,
  };

  static defaultProps = {
    infos: {},
    loading: false,
  };

  state = {
    clientError: null,
    serialNumberError: null,
    lastInspectionDateError: null,
    markError: null,
    barcodeError: null,
  };

  constructor(props) {
    super(props);

    this.updateMod = !!props.infos.uuid;
    this.lastInspectionDate =
      props.infos.lastInspectionDate &&
      new Date(props.infos.lastInspectionDate);

    this.isPressureGaugeUnique = this.isPressureGaugeUnique.bind(this);
    this.onChangeClient = this.onChangeClient.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    // Fields
    this.setSerialNumber = this.setSerialNumber.bind(this);
    this.setLastInspectionDate = this.setLastInspectionDate.bind(this);
    this.setDesignation = this.setDesignation.bind(this);
    this.setMark = this.setMark.bind(this);
    this.setBarcode = this.setBarcode.bind(this);
    this.setClient = this.setClient.bind(this);

    this.renderClientOption = this.renderClientOption.bind(this);
    this.renderClientsSelect = this.renderClientsSelect.bind(this);
  }

  componentDidMount() {
    this.props.getPressureGaugesList();
  }

  /**
   * {@inheritdoc}
   */
  componentDidUpdate(prevProps) {
    const { pressureGauge, goBack } = this.props;

    if (
      prevProps.pressureGauge.saved === pressureGauge.saved &&
      prevProps.pressureGauge.errors === pressureGauge.errors
    ) {
      return;
    }

    if (pressureGauge.errors) {
      pressureGauge.errors.map((error) =>
        this.setState({ [`${error.path}Error`]: error.reason })
      );
    }

    if (pressureGauge.saved) {
      goBack();
    }
  }

  /**
   * onChange client select
   *
   * @param {Object} event
   */
  onChangeClient(event) {
    const uuid = event.target.value;
    const { clients } = this.props;
    const selectedClient = clients.list.filter(
      (client) => client.uuid === uuid
    )[0];

    if (!selectedClient) {
      this.setState({ isCompanyClient: false });

      return;
    }

    this.setState({
      isCompanyClient: selectedClient.type === ClientTypes.COMPANY,
    });
  }

  onSubmit() {
    if (!this.isValid()) {
      return;
    }

    const newPressureGauge = {
      serialNumber: this.serialNumber.value,
      lastInspectionDate: this.lastInspectionDate.toISOString(),
      designation: this.designation.value || null,
      mark: this.mark.value || null,
      barcode: this.barcode.value || null,
    };

    if (!this.updateMod) {
      newPressureGauge.clientUuid = this.client.value;
    }

    const { pressureGauge } = this.props;

    if (
      !this.isPressureGaugeUnique(
        newPressureGauge,
        pressureGauge.infos ? pressureGauge.infos.uuid : null
      )
    ) {
      return;
    }

    this.props.onSubmit(newPressureGauge);
  }

  /**
   * @param {String} serialNumber
   */
  setSerialNumber(serialNumber) {
    this.serialNumber = serialNumber;
  }

  /**
   * @param {Date} lastInspectionDate
   */
  setLastInspectionDate(lastInspectionDate) {
    this.lastInspectionDate = lastInspectionDate;
  }

  /**
   * @param {String} designation
   */
  setDesignation(designation) {
    this.designation = designation;
  }

  /**
   * @param {String} mark
   */
  setMark(mark) {
    this.mark = mark;
  }

  /**
   * @param {String} barcode
   */
  setBarcode(barcode) {
    this.barcode = barcode;
  }

  /**
   * @param {String} client
   */
  setClient(client) {
    this.client = client;
  }

  /**
   * Validation pressure gauge unicity
   *
   * @param {Object} pressureGauge
   * @param {String} uuid
   *
   * @return {Boolean}
   */
  isPressureGaugeUnique(pressureGauge, uuid = null) {
    const { pressureGauges } = this.props;
    const clientUuid = this.client.value;
    const others = pressureGauges.list.filter(
      (pressureGauge) =>
        pressureGauge.clientUuid === clientUuid && pressureGauge.uuid !== uuid
    );

    let alreadyExists = false;

    if (
      pressureGauge.mark &&
      others.some((pressureGauge) => pressureGauge.mark === pressureGauge.mark)
    ) {
      alreadyExists = true;

      this.setState({
        markError: I18n.t("pages.pressure_gauges.add.form.mark:error:unicity"),
      });
    } else {
      this.setState({ markError: null });
    }

    if (
      pressureGauge.barcode &&
      others.some((pressureGauge) => pressureGauge.barcode === pressureGauge.barcode)
    ) {
      alreadyExists = true;

      this.setState({
        barcodeError: I18n.t(
          "pages.pressure_gauges.add.form.barcode:error:unicity"
        ),
      });
    } else {
      this.setState({ barcodeError: null });
    }

    return !alreadyExists;
  }

  /**
   * @return {Boolean}
   */
  isValid() {
    const serialNumberLength = this.serialNumber.value.length;
    const clientLength = this.client.value.length;

    const serialNumberErrorText = I18n.t(
      "pages.pressure_gauges.add.form.serial_number:error:empty"
    );
    const lastInspectionDateErrorText = I18n.t(
      "pages.pressure_gauges.add.form.last_inspection_date:error:empty"
    );
    const clientErrorText = I18n.t(
      "pages.pressure_gauges.add.form.client:error:empty"
    );

    this.setState({
      serialNumberError: !serialNumberLength ? serialNumberErrorText : null,
      lastInspectionDateError: !this.lastInspectionDate
        ? lastInspectionDateErrorText
        : null,
      clientError: !clientLength ? clientErrorText : null,
    });

    return serialNumberLength && this.lastInspectionDate && clientLength;
  }

  /**
   * @param {Object} option
   *
   * @return {Element}
   */
  renderClientOption(option) {
    return (
      <option key={`option-${option.uuid}`} value={option.uuid}>
        {ClientUtils.renderLabel(option)}
      </option>
    );
  }

  /**
   * Render clients select
   *
   * @return {Element}
   */
  renderClientsSelect() {
    const { clients, infos } = this.props;
    const { clientError } = this.state;
    let options = null;

    if (clients.list.length) {
      options = clients.list
        .filter((client) => new ClientTypes(client.type).canHaveTools())
        .sort((a, b) =>
          alphabetically(ClientUtils.renderLabel(a), ClientUtils.renderLabel(b))
        );
    }

    return (
      <Select
        id="client"
        label={I18n.t("pages.pressure_gauges.add.form.client:label")}
        ref={this.setClient}
        options={options}
        renderOption={this.renderClientOption}
        disabled={this.updateMod}
        defaultValue={(this.updateMod && infos && infos.clientUuid) || ""}
        error={clientError}
        onChange={this.onChangeClient}
      />
    );
  }

  /**
   * {@inheritdoc}
   */
  render() {
    const { loading, infos } = this.props;
    const {
      serialNumberError,
      lastInspectionDateError,
      markError,
      barcodeError,
    } = this.state;

    return (
      <form>
        {this.renderClientsSelect()}
        <TextInput
          id="serialNumber"
          label={`${I18n.t(
            "pages.pressure_gauges.add.form.serial_number:label"
          )}`}
          defaultValue={infos.serialNumber}
          ref={this.setSerialNumber}
          error={serialNumberError}
        />
        <DateInput
          id="lastInspectionDate"
          label={`${I18n.t(
            "pages.pressure_gauges.add.form.last_inspection_date:label"
          )}`}
          defaultValue={this.lastInspectionDate}
          onChange={this.setLastInspectionDate}
          error={lastInspectionDateError}
        />
        <TextInput
          id="designation"
          label={I18n.t("pages.pressure_gauges.add.form.designation:label")}
          defaultValue={infos.designation}
          ref={this.setDesignation}
          optional
        />
        <TextInput
          id="mark"
          label={I18n.t("pages.pressure_gauges.add.form.mark:label")}
          defaultValue={infos.mark}
          ref={this.setMark}
          optional
          error={markError}
        />
        <TextInput
          id="barcode"
          label={I18n.t("pages.pressure_gauges.add.form.barcode:label")}
          defaultValue={infos.barcode}
          ref={this.setBarcode}
          optional
          error={barcodeError}
        />
        <SubmitButton loading={loading} onClick={this.onSubmit} />
      </form>
    );
  }
}

const mapStateToProps = (state) => ({
  clients: state.clients,
  pressureGauge: state.pressureGauge,
  pressureGauges: state.pressureGauges,
});

const mapDispatchToProps = (dispatch) => ({
  getPressureGaugesList: () => dispatch(getPressureGaugesList()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CreatePressureGaugeForm);
